import React, { useEffect, useState } from 'react';
import { X, Download, Share, SquarePlus } from 'lucide-react';
import { trackEvent } from '../lib/analytics';

const DISMISS_KEY = 'eager_pwa_dismissed_at';
const DISMISS_DAYS = 14;

const isStandalone = () =>
    window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const isIos = () =>
    /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

const recentlyDismissed = () => {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return ts && Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
};

export default function InstallAppPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [mode, setMode] = useState(null); // 'native' | 'ios'

    useEffect(() => {
        if (isStandalone() || recentlyDismissed()) return;

        let timer;
        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferred(e);
            timer = setTimeout(() => setMode('native'), 4000);
        };
        window.addEventListener('beforeinstallprompt', onBeforeInstall);

        const onInstalled = () => {
            trackEvent('pwa', 'installed');
            setMode(null);
            localStorage.setItem(DISMISS_KEY, String(Date.now()));
        };
        window.addEventListener('appinstalled', onInstalled);

        let iosTimer;
        if (isIos()) iosTimer = setTimeout(() => setMode('ios'), 6000);

        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstall);
            window.removeEventListener('appinstalled', onInstalled);
            clearTimeout(timer);
            clearTimeout(iosTimer);
        };
    }, []);

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        trackEvent('pwa', 'install_banner_dismissed');
        setMode(null);
    };

    const install = async () => {
        if (!deferred) return;
        deferred.prompt();
        const { outcome } = await deferred.userChoice;
        trackEvent('pwa', 'install_prompt_result', outcome);
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        setMode(null);
    };

    if (!mode) return null;

    return (
        <div
            data-testid="pwa-install-banner"
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-[360px] z-[70] animate-in slide-in-from-bottom-4 fade-in duration-500"
        >
            <div className="rounded-2xl bg-[#131C2F] ring-1 ring-sky-400/25 shadow-2xl shadow-black/40 p-4">
                <div className="flex items-start gap-3">
                    <img src="/favicon-192.png" alt="EAGER" className="w-11 h-11 rounded-xl flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-black text-white leading-tight">Install the EAGER app</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">Free · opens instantly · works offline</div>
                    </div>
                    <button
                        onClick={dismiss}
                        data-testid="pwa-install-dismiss"
                        aria-label="Dismiss"
                        className="p-1 -m-1 text-slate-500 hover:text-white transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {mode === 'native' ? (
                    <button
                        onClick={install}
                        data-testid="pwa-install-button"
                        className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-sky-300 hover:bg-sky-200 text-[#131C2F] text-xs font-black rounded-xl transition-colors"
                    >
                        <Download className="w-4 h-4" /> Install app
                    </button>
                ) : (
                    <div className="mt-3 text-[11px] text-slate-300 leading-relaxed bg-white/5 rounded-xl px-3 py-2.5">
                        Tap <Share className="w-3.5 h-3.5 inline -mt-0.5 text-sky-300" /> <b>Share</b> in Safari, then{' '}
                        <SquarePlus className="w-3.5 h-3.5 inline -mt-0.5 text-sky-300" /> <b>Add to Home Screen</b>
                    </div>
                )}
            </div>
        </div>
    );
}
